import React, { useEffect, useState, useId } from 'react';
import './TimeInput.css';

interface TimeInputProps {
  id?: string;
  label?: React.ReactNode;
  description?: string;
  placeholder?: string;
  error?: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  format?: '12-hour' | '24-hour';
  style?: React.CSSProperties;
  leftSection?: React.ReactNode;
  required?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  'data-testid'?: string;
}

interface SizeStyle {
  fontSize: string;
  descriptionFontSize: string;
  height: string;
}

const sizeStyles: Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', SizeStyle> = {
  xs: {
    fontSize: '0.75rem',
    descriptionFontSize: '0.625rem',
    height: '1.875rem',
  },
  sm: {
    fontSize: '0.875rem',
    descriptionFontSize: '0.75rem',
    height: '2.25rem',
  },
  md: { fontSize: '1rem', descriptionFontSize: '0.875rem', height: '2.625rem' },
  lg: { fontSize: '1.125rem', descriptionFontSize: '1rem', height: '3.125rem' },
  xl: {
    fontSize: '1.25rem',
    descriptionFontSize: '1.125rem',
    height: '3.75rem',
  },
};

const TimeInput: React.FC<TimeInputProps> = ({
  id,
  label = '',
  description = '',
  placeholder = 'hh:mm',
  error,
  value = '',
  onChange,
  disabled = false,
  format = '24-hour',
  style,
  leftSection = <></>,
  required = false,
  size = 'sm',
  'data-testid': dataTestId = '',
}) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const [period, setPeriod] = useState<'AM' | 'PM'>('AM');
  const [localValue, setLocalValue] = useState<string>(value);
  const inputStyles = sizeStyles[size];

  useEffect(() => {
    if (value && format === '12-hour' && value.length === 5) {
      const slicedValue = value.slice(0, 5);
      const [hours, minutes] = slicedValue.split(':');
      let adjustedHours = +hours;

      if (adjustedHours > 12) {
        adjustedHours -= 12;
      }

      setPeriod(+hours >= 12 ? 'PM' : 'AM');
      const formattedTime = `${adjustedHours.toString()}:${minutes}`;
      setLocalValue(formattedTime);
    } else if (value && format === '24-hour') {
      setLocalValue(value.slice(0, 5));
    }
  }, [value, format]);

  const is24hoursValidTime = (hours: string, minutes: string) => {
    return +hours >= 0 && +hours <= 23 && +minutes >= 0 && +minutes <= 59;
  };

  const is12hoursValidTime = (hours: string, minutes: string) => {
    return +hours >= 1 && +hours <= 12 && +minutes >= 0 && +minutes <= 59;
  };

  const handle24hoursInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value.replace(/[^0-9]/g, '');
    if (inputValue.length > 4) inputValue = inputValue.slice(0, 4);

    if (inputValue.length > 2) {
      inputValue = `${inputValue.slice(0, 2)}:${inputValue.slice(2)}`;
    }

    const [hours = '', minutes = ''] = inputValue.split(':');
    if (!is24hoursValidTime(hours, minutes)) return;

    setLocalValue(inputValue);

    let formattedValue = inputValue;

    if (inputValue.length === 5) {
      formattedValue = `${hours}:${minutes}:00`;
    }
    if (onChange) onChange(formattedValue);
  };

  const handle12hoursInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value.replace(/[^0-9]/g, '');
    if (inputValue.length > 4) inputValue = inputValue.slice(0, 4);

    if (inputValue.length > 2) {
      inputValue = `${inputValue.slice(0, 2)}:${inputValue.slice(2)}`;
    }

    const [hours = '', minutes = ''] = inputValue.split(':');

    if (!is12hoursValidTime(hours, minutes) && inputValue.length > 1) return;

    setLocalValue(inputValue);

    let formattedValue = inputValue;

    if (inputValue.length === 5) {
      let hourInt = +hours;
      if (period === 'PM' && hourInt !== 12) hourInt += 12;
      if (period === 'AM' && hourInt === 12) hourInt = 0;
      const paddedHours = hourInt.toString().padStart(2, '0');

      formattedValue = `${paddedHours}:${minutes}:00`;
    }

    if (onChange) onChange(formattedValue);
  };

  const handlePeriodChange = () => {
    setPeriod(prev => {
      const newPeriod = prev === 'AM' ? 'PM' : 'AM';

      if (localValue.length === 5) {
        const [hours, minutes] = localValue.split(':');
        let hourInt: any = +hours;

        if (newPeriod === 'PM' && hourInt !== 12) hourInt += 12;
        if (newPeriod === 'AM' && hourInt === 12) hourInt = 0;

        const paddedHours = hourInt.toString().padStart(2, '0');

        const updatedValue = `${paddedHours}:${minutes}:00`;
        if (onChange) onChange(updatedValue);
      }

      return newPeriod;
    });
  };

  return (
    <div className="container-wrapper" style={style}>
      {label && (
        <label
          htmlFor={inputId}
          className="label"
          style={{ fontSize: inputStyles.fontSize }}
        >
          {label}
          {required && <span className="required-asterisk"> *</span>}
        </label>
      )}
      {description && (
        <p
          className="description"
          style={{ fontSize: inputStyles.descriptionFontSize }}
        >
          {description}
        </p>
      )}
      <div className="flex flex-row justify-start items-center">
        {leftSection}
        {format === '24-hour' ? (
          <input
            id={inputId}
            type="text"
            value={localValue}
            onChange={handle24hoursInputChange}
            placeholder={placeholder}
            maxLength={5}
            className={error ? 'error' : 'input'}
            disabled={disabled}
            style={{
              fontSize: inputStyles.fontSize,
              height: inputStyles.height,
            }}
            data-testid={dataTestId}
          />
        ) : (
          <div className="input-wrapper">
            <input
              type="text"
              value={localValue}
              onChange={handle12hoursInputChange}
              placeholder={placeholder}
              maxLength={5}
              className={error ? 'error' : 'input'}
              disabled={disabled}
              style={{
                fontSize: inputStyles.fontSize,
                height: inputStyles.height,
              }}
              data-testid={dataTestId}
            />
            {format === '12-hour' && (
              <button
                type="button"
                className="time-input-period-toggle"
                onClick={handlePeriodChange}
                disabled={disabled}
                style={{
                  fontSize: inputStyles.fontSize,
                  height: inputStyles.height,
                }}
              >
                {period}
              </button>
            )}
          </div>
        )}
        {error && <span className="error-message">{error}</span>}
      </div>
    </div>
  );
};

export default TimeInput;
